import { BigNumber, ethers } from "ethers";
import { useEffect, useMemo, useState } from "react";

import { useQuery } from "@apollo/client";
import { useWeb3React } from "@web3-react/core";

import { ETH_PRICE_QUERY } from "../constants/queries";
import { SalesInfo, SaleType } from "../constants/sale";
import { useGnarlyGoatsContracts } from "./useGnarlyGoatsContracts";
import { useTokens } from "./usePaginatedTokens";

export interface TxnOverride {
	value?: BigNumber
	gasPrice: BigNumber
}

export interface GasEstimate {
	txnOverride: TxnOverride
	gasLimit: BigNumber
	costInGwei: Number
	costInUsd: Number
	prettyCostInGwei: string
	prettyCostInEth: string
	prettyCostInUsd: string
	errReason?: boolean
}

export enum GasEstimateLevels {
	Low = 'Low',
	Market = 'Market',
	High = 'High',
}

// ratios have to be in double digits because BigNumber does not support decimals
export const GasEstimateRatios: { [level: string]: number } = {
	[GasEstimateLevels.Low]: 10,
	[GasEstimateLevels.Market]: 15,
	[GasEstimateLevels.High]: 20,
}

export type GasEstimatesMap = { [level: string]: GasEstimate }

export interface Props {
	saleType: SaleType // the type of sale (e.g. ClaimMintPasses, RedeemMintPasses)
	mintAmount: number // the amoount of tokens to include in the tranaction estimate
}

export function useGasEstimates({ saleType, mintAmount }: Props) {
	const { library } = useWeb3React()
	const { contract, mintPassContract } = useGnarlyGoatsContracts()

	const { mintPassTokens } = useTokens()

	/**
	 * Load the current price of ether in dollars using Apollo
	 */
	const { loading: ethLoading, data: ethPriceData } = useQuery(ETH_PRICE_QUERY)

	const ethPrice = useMemo(() => {
		try {
			const ethPriceUSD = ethPriceData.bundle.ethPrice as number
			return BigNumber.from(Math.round(ethPriceUSD))
		} catch (err) {
			return BigNumber.from(0)
		}
	}, [ethLoading])

	const [gasEstimatesMap, setGasEstimatesMap] = useState<
		GasEstimatesMap | undefined
	>(undefined)

	const _setGasEstimatesMap = async () => {
		let txnOverride: TxnOverride = {
			gasPrice: await library.getGasPrice(),
		}

		// if the sale has a transaction ether value, calculate the mint price
		if (SalesInfo[saleType].tokenPriceInEth !== undefined) {
			const mintPrice = SalesInfo[saleType].tokenPriceInEth as number
			const mintValue = ethers.utils.parseEther(String(mintPrice * mintAmount))
			txnOverride.value = mintValue
		}

		var gasLimit = BigNumber.from(0)

		// try to estimate the gas limit for the selected type of transaction
		try {
			switch (saleType) {
				case SaleType.ClaimMintPasses:
					gasLimit = (await mintPassContract?.estimateGas.claimMintPasses(
						mintAmount,
						txnOverride
					)) as BigNumber
					break
				case SaleType.RedeemMintPasses:
					let tokenIds = mintPassTokens?.map((token) => {
						return BigNumber.from(token.tokenId)
					})
					gasLimit = (await contract?.estimateGas.redeemMintPasses(
						tokenIds
					)) as BigNumber
					break
				case SaleType.ClaimWhitelistGoats:
					gasLimit = (await contract?.estimateGas.claimWhitelistGoats(
						mintAmount,
						txnOverride
					)) as BigNumber
					break
				case SaleType.MintGoats:
					gasLimit = (await contract?.estimateGas.mintGoats(
						mintAmount,
						txnOverride
					)) as BigNumber
					break
			}
		} catch (err) {
			console.log('Error estimating gas:', err)
		}

		console.log(`Estimated gas limit [${gasLimit}] for [${saleType}]`)

		updateGasEstimatesMap(txnOverride, gasLimit)
	}

	/**
	 * Calculate the GasEstimatesMap when the mintAmount
	 * or ethPrice changes.
	 */
	useEffect(() => {
		_setGasEstimatesMap()
	}, [mintAmount, saleType, ethPrice])

	const updateGasEstimatesMap = (
		txnOverride: TxnOverride,
		gasLimit: BigNumber
	): GasEstimatesMap | undefined => {
		if (gasLimit.eq(0) || ethPrice.eq(0)) {
			// console.log(
			// 	`Cannot calculate gas estimates map with gas limit [${gasLimit}] and ETH price [${ethPrice}]`
			// )
			setGasEstimatesMap(undefined)
			return undefined
		}

		var _gasEstimatesMap: GasEstimatesMap = {}

		for (const level in GasEstimateLevels) {
			var limit = gasLimit.mul(GasEstimateRatios[level]).div(10) // divide by 10 to get accurate ratio

			try {
				const costInWei = txnOverride.gasPrice.mul(limit)
				const costInGwei = ethers.utils.formatUnits(costInWei, 'gwei')
				const costInEth = parseFloat(ethers.utils.formatEther(costInWei))
				const costInUsd = costInEth * ethPrice.toNumber()

				var _txnOverride = {
					value: txnOverride.value,
					gasPrice: txnOverride.gasPrice.mul(GasEstimateRatios[level]).div(10),
				}

				_gasEstimatesMap[level] = {
					txnOverride: _txnOverride,
					gasLimit: limit,
					costInGwei: Number(costInGwei),
					costInUsd: costInUsd,
					prettyCostInGwei: `${Number(costInGwei)
						.toFixed(0)
						.toString()
						.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} gwei`,
					prettyCostInEth: `${ethers.constants.EtherSymbol} ${costInEth.toFixed(
						5
					)}...`,
					prettyCostInUsd: `~ ${costInUsd.toLocaleString('en-US', {
						style: 'currency',
						currency: 'USD',
					})}`,
				}
			} catch (err) {
				console.log('Error generating gas estimates map.')
				setGasEstimatesMap(undefined)
			}
		}

		setGasEstimatesMap(_gasEstimatesMap)
	}

	return {
		gasEstimatesMap,
	}
}
